import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { defaultExploreState, readExploreSession, resetExploreSession, writeExploreSession } from '../utils/session';
import { EXPLORE_STEPS } from '../utils/routes';
import { getExploreProgress } from '../utils/progress';

const ExploreContext = createContext(null);

export function ExploreProvider({ children }) {
  const [state, setState] = useState(() => {
    const existing = readExploreSession();
    return {
      ...existing,
      startedAt: existing.startedAt || new Date().toISOString(),
    };
  });

  useEffect(() => {
    writeExploreSession(state);
  }, [state]);

  const api = useMemo(() => {
    const update = (patch) => setState((current) => ({ ...current, ...patch }));

    const visitStep = (stepId) => {
      setState((current) => {
        const visited = current.visitedSteps || [];
        if (current.currentStep === stepId && visited.includes(stepId)) return current;
        return {
          ...current,
          currentStep: stepId,
          visitedSteps: Array.from(new Set([...visited, stepId])),
        };
      });
    };

    const completeStep = (stepId) => {
      setState((current) => ({
        ...current,
        completedSteps: Array.from(new Set([...(current.completedSteps || []), stepId])),
      }));
    };

    const skipStep = (stepId) => {
      setState((current) => ({
        ...current,
        skippedSteps: Array.from(new Set([...(current.skippedSteps || []), stepId])),
      }));
    };

    const setAnswer = (key, value) => {
      setState((current) => ({
        ...current,
        answers: { ...(current.answers || {}), [key]: value },
      }));
    };

    const reset = () => {
      resetExploreSession();
      setState({
        ...defaultExploreState,
        startedAt: new Date().toISOString(),
      });
    };

    return {
      state,
      steps: EXPLORE_STEPS,
      progress: getExploreProgress(state.currentStep, state.completedSteps),
      update,
      visitStep,
      completeStep,
      skipStep,
      setAnswer,
      reset,
    };
  }, [state]);

  return <ExploreContext.Provider value={api}>{children}</ExploreContext.Provider>;
}

export function useExploreContext() {
  const context = useContext(ExploreContext);
  if (!context) {
    throw new Error('useExploreContext must be used inside ExploreProvider');
  }
  return context;
}
